//https://advenage.com/topics/angular-rendering-barcodes-and-qr-codes-with-zxing-wasm
import { inject, Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { writeBarcode, WriterOptions } from "zxing-wasm/writer";
/**
 * Service to generate barcode images using ZXing WASM
 */
@Injectable({
	providedIn: 'root'
})
export class BarcodeService {

	/**
	 * DI
	 */
	private readonly sanitizer = inject(DomSanitizer);

	/**
	 * Generated barcode image as SVG
	 *
	 * @param text is the text to encode
	 * @param writerOptions are the options for the writer
	 * @returns a promise that resolves to a DataURL as SafeUrl or null
	 */
	async getBarcodeSvg(text: string, writerOptions?: WriterOptions): Promise<string | null> {
		if (!text || text.length === 0) {
			return null;
		}
		try {
			const writeResult = await writeBarcode(text, writerOptions);
			if (writeResult.error) {
				return null;
			} else if (!writeResult.svg) {
				return null;
			} else {
				const res = writeResult.svg;
				return res;
			}
		} catch (error) {
			return null;
		}
	}

	/**
	 * Generates a barcode in SVG format and returns it as a Blob.
	 *
	 * @param text - The text to encode into the barcode. Must be a non-empty string.
	 * @param writerOptions - Optional configuration options for the barcode writer.
	 * @returns A Promise that resolves to a Blob containing the SVG representation of the barcode,
	 *          or `null` if the input text is invalid or the SVG generation fails.
	 * @throws Will log an error to the console if the input text is empty or undefined.
	 */
	async getBarcodeSvgAsBlob(text: string, writerOptions?: WriterOptions): Promise<Blob | null> {
		if (!text || text.length === 0) {
			return null;
		}
		const svg = await this.getBarcodeSvg(text, writerOptions);
		if (svg) {
			const blob = new Blob([svg], { type: 'image/svg+xml' });
			return blob;
		}
		return null;
	}

	/**
	 * Generates a barcode in SVG format and returns it as a `SafeUrl`.
	 *
	 * This method takes a text input, generates a barcode in SVG format, 
	 * and wraps it in a `SafeUrl` to ensure it is safe for use in Angular 
	 * templates. If the input text is empty or null, the method logs an 
	 * error and returns `null`.
	 *
	 * @param text - The text to encode in the barcode.
	 * @param writerOptions - Optional configuration for the barcode writer.
	 * @returns A promise that resolves to a `SafeUrl` containing the barcode SVG, 
	 *          or `null` if the input text is invalid or an error occurs.
	 */
	async getBarcodeSvgAsSafeUrl(text: string, writerOptions?: WriterOptions): Promise<SafeUrl | null> {
		if (!text || text.length === 0) {
			return null;
		}
		const svg = await this.getBarcodeSvg(text, writerOptions);
		if (svg) {
			const blob = new Blob([svg], { type: 'image/svg+xml' });
			return blob ? this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob)) : null;
		}
		return null;
	}

	/**
	 * Generates a barcode image as a Blob from the provided text.
	 *
	 * @param text - The text to encode into the barcode. Must be a non-empty string.
	 * @param writerOptions - Optional configuration options for the barcode writer.
	 * @returns A promise that resolves to a Blob containing the barcode image, or `null` if an error occurs.
	 *
	 * @throws Will log an error to the console if:
	 * - The `text` parameter is empty or null.
	 * - The barcode generation fails due to an internal error.
	 * - The generated barcode image is missing.
	 */
	async getBarcodeBlob(text: string, writerOptions?: WriterOptions): Promise<Blob | null> {
		if (!text || text.length === 0) {
			return null;
		}
		try {
			const writeResult = await writeBarcode(text, writerOptions);
			if (writeResult.error) {
				return null;
			} else if (!writeResult.image) {
				return null;
			} else {
				const res = writeResult.image;
				return res;
			}
		} catch (error) {
			return null;
		}
	}

	/**
	 * Generates a Data URL representation of a barcode for the given text.
	 *
	 * @param text - The text to encode into the barcode. Must be a non-empty string.
	 * @param writerOptions - Optional configuration options for the barcode writer.
	 * @returns A promise that resolves to a Data URL string representing the barcode,
	 *          or `null` if the text is invalid or an error occurs during processing.
	 *
	 * @throws Will log an error to the console if the input text is empty or undefined.
	 */
	async getBarcodeDataUrl(text: string, writerOptions?: WriterOptions): Promise<string | null> {
		if (!text || text.length === 0) {
			return null;
		}
		const blob = await this.getBarcodeBlob(text, writerOptions);
		if (blob) {
			const reader = new FileReader();
			const res = new Promise<string | null>((resolve) => {
				reader.onload = () => {
					resolve(reader.result as string);
				};
				reader.onerror = () => {
					resolve(null);
				};
			});
			reader.readAsDataURL(blob);
			return res;
		} else {
			return null;
		}
	}

	/**
	 * Generates a barcode as a SafeUrl from the provided text and writer options.
	 *
	 * This method creates a barcode blob using the `getBarcodeBlob` method and 
	 * converts it into a SafeUrl using Angular's `DomSanitizer` to bypass security 
	 * restrictions. If the input text is empty or null, it logs an error and returns null.
	 *
	 * @param text - The text to encode into the barcode. Must be a non-empty string.
	 * @param writerOptions - Optional configuration options for the barcode writer.
	 * @returns A Promise that resolves to a SafeUrl containing the barcode, or null if 
	 *          the input text is invalid or the barcode generation fails.
	 */
	async getBarcodeSafeUrl(text: string, writerOptions?: WriterOptions): Promise<SafeUrl | null> {
		if (!text || text.length === 0) {
			return null;
		}
		const res = await this.getBarcodeBlob(text, writerOptions);
		return res ? this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(res)) : null;
	}


	/**
	 * Generates an `ImageBitmap` representation of a barcode for the given text.
	 *
	 * @param text - The text to encode into a barcode. Must be a non-empty string.
	 * @param writerOptions - Optional configuration for the barcode writer, such as format or dimensions.
	 * @returns A promise that resolves to an `ImageBitmap` of the generated barcode, or `null` if the input text is invalid or an error occurs.
	 *
	 * @throws Will log an error to the console if the input text is empty or null.
	 */
	async getBarcodeImageBitmap(text: string, writerOptions?: WriterOptions): Promise<ImageBitmap | null> {
		if (!text || text.length === 0) {
			return null;
		}
		const blob = await this.getBarcodeBlob(text, writerOptions);
		if (!blob) {
			return null;
		}
		const imageBitMap = await createImageBitmap(blob);
		return imageBitMap;
	}
}
